import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';

import { http } from './http';

export interface CallerCommunicationLink {
  _id: string;
  cfs: string;
  token: string;
  phoneNumber: string;
  callerId: string;
  status: 'NOT_SENT' | 'SENT' | 'ACCEPTED' | 'EXPIRED';
  smsMessageId?: string;
  expiresAt?: string;
  sentAt?: string;
  acceptedAt?: string;
  fullName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CallerLocation {
  _id: string;
  callerLink: string;
  cfs: string;
  point: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  accuracy?: number;
  altitude?: number;
  heading?: number;
  speed?: number;
  deviceTimestamp: string;
  address?: {
    _id: string;
    label?: string; // from Ably real-time event
    normalized?: string; // from REST API (virtual field)
    addressLabel?: string; // from REST API fallback
  };
  createdAt: string;
  isNew?: boolean;
}

export interface CallerFile {
  _id: string;
  callerLink: string;
  cfs: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  mediaType: 'PHOTO' | 'VIDEO' | 'DOCUMENT' | 'AUDIO';
  url: string;
  thumbnailUrl?: string;
  createdAt: string;
}

export interface RawCallerFile extends Partial<CallerFile> {
  name?: string;
  size?: number;
  mimetype?: string;
}

export const normalizeCallerFile = (raw: RawCallerFile): CallerFile => ({
  _id: raw._id ?? '',
  callerLink: raw.callerLink ?? '',
  cfs: raw.cfs ?? '',
  fileName: raw.fileName ?? raw.name ?? '',
  fileSize: raw.fileSize ?? raw.size ?? 0,
  mimeType: raw.mimeType ?? raw.mimetype ?? '',
  mediaType: raw.mediaType ?? 'DOCUMENT',
  url: raw.url ?? '',
  thumbnailUrl: raw.thumbnailUrl,
  createdAt: raw.createdAt ?? '',
});

export interface CallerMessage {
  _id: string;
  callerLink: string;
  cfs: string;
  sender: 'CALLER' | 'DISPATCHER' | 'SYSTEM';
  senderUser?: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

export interface CreateCallerParams {
  phoneNumber: string;
}

export interface SendMessageParams {
  content: string;
}

export const CallerCommunicationApi = {
  /**
   * Get all callers for a CFS
   */
  getCallers: async (cfsId: string): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers`,
    );
  },

  getUnreadCounts: async (cfsId: string): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/unread-counts`,
    );
  },

  /**
   * Add a new caller to a CFS
   */
  addCaller: async (
    cfsId: string,
    params: CreateCallerParams,
  ): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers`,
      params,
    );
  },

  /**
   * Send SMS link to a caller
   */
  sendSmsLink: async (
    cfsId: string,
    callerId: string,
  ): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/send-sms`,
    );
  },

  /**
   * Resend SMS link to a caller
   */
  resendSmsLink: async (
    cfsId: string,
    callerId: string,
  ): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/resend`,
    );
  },

  /**
   * Get locations for a caller
   */
  getLocations: async (
    cfsId: string,
    callerId: string,
  ): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/locations`,
    );
  },

  getNewLocationCounts: async (cfsId: string): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/new-location-counts`,
    );
  },

  markAllLocationsSeen: async (cfsId: string): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/mark-all-locations-seen`,
    );
  },

  /**
   * Get files for a caller
   */
  getFiles: async (cfsId: string, callerId: string): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/files`,
    );
  },

  /**
   * Get a short-lived SAS download URL for a caller file
   */
  getFileUrl: async (
    cfsId: string,
    callerId: string,
    fileId: string,
  ): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/files/${fileId}/url`,
    );
  },

  /**
   * Get messages for a caller
   */
  getMessages: async (
    cfsId: string,
    callerId: string,
    limit = 50,
    before?: string,
  ): Promise<AxiosResponse> => {
    return http.get(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/messages`,
      { params: { limit, before } },
    );
  },

  /**
   * Send message to a caller (from dispatcher)
   */
  sendMessage: async (
    cfsId: string,
    callerId: string,
    params: SendMessageParams,
  ): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/cfs/${cfsId}/caller-communication/callers/${callerId}/messages`,
      params,
    );
  },
};
